<template>
  <v-card>
    <v-card-title :class="contractColor">
      <v-row justify="space-between" align="center">
        <v-col cols="auto">
          <span class="headline">{{ title }} </span>
        </v-col>

        <v-col cols="auto">
          <BaseDialogActions hideRefresh/>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-6">
      <v-row>
        <v-col cols="12" lg="2">
          <ReadOnlyAttributeCard
            :label="$t('category')"
            :value="AccountingCategory.fromInt(activityBuilder.activityTemplate.category)"/>
        </v-col>

        <v-col cols="12" lg="5">
          <ReadOnlyAttributeCard
            :label="$t('template')"
            :value="activityBuilder.activityTemplate.name">
              <template #prepend-inner v-if="specialization !== TemplateSpecialization.None">
                <Icon
                :icon="specialization.icon"
                :tooltipText="specialization.name"
                :small="false"
                icon-color="black"
                style="pointer-events: auto;"/>
              </template>
            </ReadOnlyAttributeCard>
        </v-col>

        <v-col cols="12" lg="5">
          <ReadOnlyAttributeCard
            :label="$t('counterparty')"
            :value="counterparty">
            <template #prepend-inner v-if="showRuntimeCounterpartyIcon">
              <Icon icon="mdi-alpha-r-circle-outline" :small="false"/>
            </template>
          </ReadOnlyAttributeCard>
        </v-col>

        <v-col cols="12" lg="4" v-if="activity.recoveryMode !== RecoveryMode.None.value && activity.recoveryMode?.value !== RecoveryMode.None.value">
          <ReadOnlyAttributeCard
            :label="$t('recoveryMode')"
            :value="recoveryModeString(activity)">
          </ReadOnlyAttributeCard>
        </v-col>

        <v-col cols="12" lg="8" v-if="activity.recoveryMode === RecoveryMode.Specific.value">
          <ReadOnlyAttributeCard
            :label="$t('advance')"
            :value="activity.advanceNotes">
            <template #prepend-inner>
              <Icon icon="mdi-cash" :small="false"/>
            </template>
          </ReadOnlyAttributeCard>
        </v-col>

        <v-col cols="12" v-if="activityDeductsFrom">
          <ReadOnlyAttributeCard
            :label="$t('deducts')"
            :value="deductionText">
            <template #prepend-inner>
              <Icon icon="mdi-circle-multiple-outline" :small="false" icon-color="success"/>
            </template>
          </ReadOnlyAttributeCard>
        </v-col>

        <v-col cols="12" v-if="canEditDeductingActivities">
          <span class="title">{{ $t('paymentDeductions') }}</span>
        </v-col>
        <v-col cols="11" class="mt-n5" v-if="canEditDeductingActivities">
          <v-autocomplete
            :label="$t('deductingActivities')"
            :items="deductionEligibleActivities"
            multiple
            chips
            v-model="selectedDeductingActivities"
            :item-text="deductingActivityText"
            return-object/>
        </v-col>

        <v-col cols="12" v-if="isDistanceTraveledActivity" class="pt-0 pb-2">
          <v-chip
            small
            label
            color="info"
            text-color="white"
            data-testid="activity-distance-traveled-indicator">
            <v-icon left small>mdi-map-marker-distance</v-icon>
            {{ distanceTraveledIndicatorText }}
          </v-chip>
        </v-col>

        <v-col cols="12" v-if="canDisplayBaseCost">
          <BaseCostForm
            id="activity-base-cost"
            :propBaseCost="activityBuilder.baseCost"
            :accountingCategory="activity?.activityTemplate?.category"
            :editing="true"
            :isBaseCostForm="true"
            :isLogsContract="contractType === ContractType.Production.value || contractType === ContractType.WoodsSale.value"
            :contract="contract"
            allowRateSheet
            :accountId="contract.destinationAccountId"
            :activity="activityBuilder"
            :focus="focusBaseRate"
            :payByTypes="baseCostPayByTypes"
            @base-cost-changed="baseCostChanged"
            :hidePayBy="specialization === TemplateSpecialization.Obligation"
            :hideRate="specialization === TemplateSpecialization.Obligation"
          />
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="py-4 px-0">
      <v-row justify="end">
        <v-col cols="auto">
          <v-btn @click="confirmClicked" :class="contractColor">
            {{ $t('confirm') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  AccountingCategory, RuntimeCounterparty, PayBy, ContractType,
  RecoveryMode, TemplateSpecialization, CostType, TractTypeCategory,
  ActivityModifier
} from '@/utils/Enumerations'
import { cloneDeep } from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import BaseCost from '@/model/BaseCost.js'
import { colorClassForContractType } from '@/utils/componentHelpers.js'

export default {
  name: 'ContractActivityScheduledChangeForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ReadOnlyAttributeCard: () => import('@/components/helper/ReadOnlyAttributeCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseCostForm: () => import('@/components/activity/BaseCostForm.vue')
  },

  props: {
    contract: {
      type: Object,
      required: true
    },

    activity: {
      type: Object,
      required: true
    },

    isImmediateRateChange: {
      type: Boolean,
      default: false
    },

    allActivities: {
      type: Array,
      default: () => []
    },

    tract: {
      type: Object,
      default: undefined
    }
  },

  data: () => ({
    TemplateSpecialization,
    AccountingCategory,
    RecoveryMode,
    ContractType,
    activityBuilder: null,
    rateSheetFetched: false,
    focusBaseRate: false,
    selectedDeductingActivities: [],
    originallyHadDeductions: false
  }),

  computed: {
    ...mapGetters('rate-sheet-definition', ['accountRateSheetDefinitions', 'accountRateSheetDefinitionsLoading']),

    title () {
      if (this.isImmediateRateChange) {
        return `${this.$t('editActivity')} - ${this.activity.activityTemplate.name}`
      }
      return `${this.$t('scheduleRateChanges')} - ${this.activity.activityTemplate.name}`
    },

    counterparty () {
      if (this.activity.contractor) {
        return this.activity.contractor.name
      } else {
        return RuntimeCounterparty.fromInt(this.activity.runtimeCounterparty)
      }
    },

    showRuntimeCounterpartyIcon () {
      return this.activity.runtimeCounterparty != null
    },

    allowToDefineRateSheet () {
      return this.activity?.baseCost?.payBy === PayBy.Volume.value && !this.accountRateSheetDefinitionsLoading
    },

    contractType () {
      return this.contract?.type
    },

    isStumpageTract () {
      return this.tract?.type?.category === TractTypeCategory.Stumpage.value
    },

    canDisplayBaseCost () {
      if (this.activity.baseCost?.rateSheetId > 0 && !this.rateSheetFetched && this.activity.baseCost?.rateSheet.rateSheetLines.length === 0) {
        return false
      }
      return this.activity?.baseCost != null
    },

    canEditDeductingActivities () {
      const anotherActivityAlreadyDeducts = this.allActivities.some(a => {
        if (this.activitiesMatch(a, this.activity)) return false
        return a.deductFromActivityId != null || a.deductFromActivityIdentifier != null
      })
      return this.contractType === ContractType.Production.value &&
        !this.isStumpageTract &&
        this.activity.activityTemplate.category === AccountingCategory.Payable.value &&
        this.activity.activityTemplate.costType === CostType.Production.value &&
        !this.activityDeductsFrom &&
        this.activity.activityTemplate.specialization !== TemplateSpecialization.Penalty.value &&
        (!anotherActivityAlreadyDeducts || this.originallyHadDeductions)
    },

    activityDeductsFrom () {
      return this.activity.deductFromActivityId != null || this.activity.deductFromActivityIdentifier != null
    },

    deductionText () {
      let parentActivity
      if (this.activity.deductFromActivityId) {
        parentActivity = this.allActivities.find(a => a.activityId === this.activity.deductFromActivityId)
      } else if (this.activity.deductFromActivityIdentifier) {
        const identifierAsActivity = {
          contractor: { accountId: this.activity.deductFromActivityIdentifier.accountId },
          runtimeCounterparty: this.activity.deductFromActivityIdentifier.runtimeCounterparty,
          activityTemplate: { activityTemplateId: this.activity.deductFromActivityIdentifier.activityTemplateId }
        }
        parentActivity = this.allActivities.find(a => this.activitiesMatch(a, identifierAsActivity))
      }
      if (!parentActivity) return ''
      const counterpartyString = this.counterpartyStringForActivity(parentActivity)
      return `${parentActivity.activityTemplate.name} - ${counterpartyString}`
    },

    deductionEligibleActivities () {
      return this.allActivities
        .filter(a => {
          const sameBusinessEntity = a.activityTemplate.businessEntityId === this.activity.activityTemplate.businessEntityId
          const notSelf = !this.activitiesMatch(a, this.activity)
          const isPayable = a.activityTemplate.category === AccountingCategory.Payable.value
          const isNoneSpecialization = a.activityTemplate.specialization === TemplateSpecialization.None.value
          const isProduction = a.activityTemplate.costType === CostType.Production.value
          return sameBusinessEntity && notSelf && isPayable && isNoneSpecialization && isProduction
        })
    },

    specialization () {
      return TemplateSpecialization.forInt(this.activity.activityTemplate.specialization)
    },

    contractColor () {
      return colorClassForContractType(this.contract.type)
    },

    isDistanceTraveledActivity () {
      return this.activity?.activityTemplate?.modifier === ActivityModifier.DistanceTraveled.value
    },

    distanceTraveledIndicatorText () {
      return this.contract?.distance > 0
        ? this.$tc('paysOnDistanceTraveledWithMiles', this.contract.distance, { distance: this.contract.distance })
        : this.$t('paysOnDistanceTraveled')
    },

    baseCostPayByTypes () {
      if (!this.isDistanceTraveledActivity) return undefined
      const isReceivable = this.activity?.activityTemplate?.category === AccountingCategory.Receivable.value
      return PayBy.enums.filter(payBy =>
        payBy !== PayBy.Load &&
        payBy !== PayBy.Volume &&
        (payBy !== PayBy.MetricWeight || isReceivable)
      )
    }
  },

  async created () {
    this.activityBuilder = cloneDeep(this.activity)
    if (this.activity.baseCost) {
      this.activityBuilder.baseCost = new BaseCost(this.activity.baseCost)
    }

    this.selectedDeductingActivities = this.allActivities.filter(a => {
      if (a.deductFromActivityId && this.activity.activityId) {
        return a.deductFromActivityId === this.activity.activityId
      }
      if (a.deductFromActivityIdentifier) {
        const identifierAsActivity = {
          contractor: { accountId: a.deductFromActivityIdentifier.accountId },
          runtimeCounterparty: a.deductFromActivityIdentifier.runtimeCounterparty,
          activityTemplate: { activityTemplateId: a.deductFromActivityIdentifier.activityTemplateId }
        }
        return this.activitiesMatch(this.activity, identifierAsActivity)
      }
      return false
    })

    this.originallyHadDeductions = this.selectedDeductingActivities.length > 0

    if (this.allowToDefineRateSheet) {
      if (this.activity.baseCost?.rateSheetId !== null && this.activity.baseCost?.rateSheetId !== undefined && this.activity.baseCost?.rateSheet?.rateSheetLines.length === 0 && this.activity.baseCost?.rateSheetId !== 0) {
        const rateSheetResult = await this.fetchRateSheet(this.activity.baseCost?.rateSheetId)
        this.activityBuilder.baseCost.rateSheet = rateSheetResult
        this.rateSheetFetched = true
      }
    }
  },

  mounted () {
    this.$nextTick(() => {
      this.focusBaseRate = true
    })
  },

  methods: {
    ...mapActions('rate-sheet', ['fetchRateSheet']),

    confirmClicked () {
      this.activityBuilder.deductingActivities = this.selectedDeductingActivities
      this.$emit('confirmed', this.activityBuilder)
    },

    baseCostChanged (newBaseCost) {
      this.activityBuilder.baseCost = cloneDeep(newBaseCost)
    },

    recoveryModeString (activity) {
      let rm = activity.recoveryMode
      if (rm?.value) {
        rm = rm.value
      }
      switch (rm) {
        case RecoveryMode.Oldest.value:
          return RecoveryMode.Oldest.name
        case RecoveryMode.OldestOnTract.value:
          return RecoveryMode.OldestOnTract.name
        case RecoveryMode.Specific.value:
          return RecoveryMode.Specific.name
      }
      return ''
    },

    activitiesMatch (a, b) {
      if (a.activityId && b.activityId) {
        return a.activityId === b.activityId
      } else {
        return a.activityTemplate?.activityTemplateId === b.activityTemplate?.activityTemplateId &&
          a.contractor?.accountId === b.contractor?.accountId &&
          a.runtimeCounterparty === b.runtimeCounterparty
      }
    },

    deductingActivityText (activity) {
      return `${activity.activityTemplate.name}, ${this.counterpartyStringForActivity(activity)}`
    },

    counterpartyStringForActivity (activity) {
      if (activity.contractor?.name) return activity.contractor.name
      switch (activity.runtimeCounterparty) {
        case RuntimeCounterparty.Landowner.value:
          return this.$t('landOwner')
        case RuntimeCounterparty.Driver.value:
          return this.$t('ticketDriver')
        default:
          return this.$t('notSet')
      }
    }
  }
}
</script>
